import { isAxiosError } from "axios";
import { useState } from "react";
import toast from "react-hot-toast";
import apiClient from "@/lib/config/axiosConfig";
import type { SuccessResponse } from "@/types/api";
import { FilterState } from "../components/Filter/Filter";
// Import the new configuration and helper functions
import { SEARCH_SOURCES } from "../config/searchSources";
import { SearchResult } from "../types";

// No longer need the hardcoded SEARCH_INDEXES constant here
// const SEARCH_INDEXES = [...] as const;

// Define the initial filter state
const initialFilterState: FilterState = {
	type: "all",
};

// Custom hook for search functionality
export const useSearch = () => {
	const [searchQuery, setSearchQuery] = useState("");
	const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
	const [isSearching, setIsSearching] = useState(false);
	const [isSearchVisible, setIsSearchVisible] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalResults, setTotalResults] = useState(0);
	const [resultsPerPage, setResultsPerPage] = useState(10);
	const [filterState, setFilterState] =
		useState<FilterState>(initialFilterState);

	const handleSearch = async (
		query: string = searchQuery,
		page: number = 1,
		currentFilter: FilterState = filterState,
		filePathFilter: string = "",
	) => {
		setIsSearching(true);

		const from = (page - 1) * resultsPerPage;

		// Determine filterIds based on the filter
		let filterIds: string[] = [];

		switch (currentFilter.type) {
			case "sharepoint":
				filterIds = ["SP"];
				break;
			case "citrixAll":
				// Get all citrix filter IDs
				filterIds = SEARCH_SOURCES.filter(
					(source) => source.type === "citrix",
				).map((source) => source.filterId);
				break;
			case "citrixSpecific":
				filterIds = currentFilter.specificDrives || [];
				break;
			default:
				// All filter IDs
				filterIds = SEARCH_SOURCES.map((source) => source.filterId);
				break;
		}

		// Check for empty filter IDs
		if (filterIds.length === 0) {
			console.warn("No filter IDs determined based on the current filter.");
			setIsSearching(false);
			setSearchResults([]);
			setTotalResults(0);

			const message =
				currentFilter.type === "citrixSpecific" &&
				!currentFilter.specificDrives?.length
					? "Please select specific network drive to filter."
					: "No valid search sources selected based on the filter.";

			toast.error(message);
			return;
		}

		try {
			const searchPayload: {
				searchTerm: string;
				filterIds: string[];
				from: number;
				size: number;
				file_path_filter?: string;
			} = {
				searchTerm: query,
				filterIds: filterIds,
				from,
				size: resultsPerPage,
			};

			// Add file_path_filter if provided
			if (filePathFilter.trim()) {
				// Escape backslashes for Elasticsearch
				const escapedFilePathFilter = filePathFilter
					.trim()
					.replace(/\\/g, "\\\\");
				searchPayload.file_path_filter = escapedFilePathFilter;
			}

			const response = await apiClient.post<
				SuccessResponse<{
					total: number;
					results: SearchResult[];
					meta: unknown;
				}>
			>("/search/search", searchPayload);
			const searchData = response.data.data;

			setSearchResults(searchData.results);
			setTotalResults(searchData.total || searchData.results.length);
			setCurrentPage(page);
			setIsSearching(false);
			setIsSearchVisible(true);
			return searchData;
		} catch (error: unknown) {
			setIsSearching(false);
			setSearchResults([]);
			setTotalResults(0);
			setIsSearchVisible(false);

			if (isAxiosError(error) && error.response) {
				const status = error.response.status;
				if (status >= 400 && status < 500) {
					toast.error(
						"Invalid search query or parameters. Please check your input.",
					);
				} else if (status >= 500) {
					toast.error("Server error occurred. Please try again later.");
				}
			} else {
				toast.error("Search failed. Please check your connection.");
			}

			console.error("Search error:", error);
		}
	};

	const clearSearch = () => {
		setSearchQuery("");
		setSearchResults([]);
		setIsSearchVisible(false);
		setCurrentPage(1);
		setFilterState(initialFilterState);
	};

	const handleFilterChange = (
		newFilterState: FilterState,
		filePathFilter: string = "",
	) => {
		setFilterState(newFilterState);
		if (searchQuery) {
			handleSearch(searchQuery, 1, newFilterState, filePathFilter);
		}
	};

	return {
		searchQuery,
		setSearchQuery,
		searchResults,
		isSearching,
		isSearchVisible,
		setIsSearchVisible,
		handleSearch,
		clearSearch,
		filterState,
		handleFilterChange,
		pagination: {
			currentPage,
			totalResults,
			resultsPerPage,
			setCurrentPage,
			setResultsPerPage,
			totalPages: Math.ceil(totalResults / resultsPerPage),
		},
	};
};
